import type { ReactElement } from 'react';

/**
 * The feeling icons, traced from the client's reference.
 *
 * Drawn as inline SVG rather than cut from the screenshot: they appear there at roughly
 * 26 px, so any crop would be mush on a retina display. Every one inherits `currentColor`,
 * which the card sets from its own tint — that is what gives the grid its eight colours.
 */
const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function Cloud() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        {...S}
        d="M7.5 17.5h9a3.6 3.6 0 0 0 .5-7.16 5.2 5.2 0 0 0-9.86-1.2A3.98 3.98 0 0 0 7.5 17.5z"
      />
      {/* the thought that will not settle */}
      <path {...S} strokeWidth={1.3} d="M11 12.6c1.6-.9 2.6.6 1.4 1.3-1 .6-.4 1.6.8 1.2" />
    </svg>
  );
}

function Heart() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...S} d="M12 19.5 4.9 12.6a4.4 4.4 0 1 1 7.1-5 4.4 4.4 0 1 1 7.1 5z" />
    </svg>
  );
}

/** The one solid mark in the set, as in the reference. */
function Bolt() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M13.6 2.5 5.8 13.2h5l-.9 8.3 8.2-11h-5.2z" />
    </svg>
  );
}

function People() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle {...S} cx="9" cy="8.4" r="2.9" />
      <circle {...S} cx="16.4" cy="9.1" r="2.3" />
      <path {...S} d="M3.6 19.2a5.4 5.4 0 0 1 10.8 0" />
      <path {...S} d="M15.1 14.4a4.4 4.4 0 0 1 5.3 4.8" />
    </svg>
  );
}

function Person() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle {...S} cx="12" cy="8.2" r="3.2" />
      <path {...S} d="M5.4 19.6a6.6 6.6 0 0 1 13.2 0" />
    </svg>
  );
}

function Bars() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        {...S}
        strokeWidth={2.1}
        d="M5.6 19.4v-4.2M10.5 19.4V9.6M15.4 19.4v-6.8M20.3 19.4V5.8"
      />
    </svg>
  );
}

function Leaf() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        {...S}
        d="M19.4 4.6C10.8 4 5.2 8 5.2 13.4a5.7 5.7 0 0 0 5.6 5.8c5.2 0 8.9-5.6 8.6-14.6z"
      />
      <path {...S} strokeWidth={1.3} d="M16.6 7.6 7.8 16.8" />
    </svg>
  );
}

function Moon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...S} d="M20 14.6A8.6 8.6 0 0 1 9.4 4a8.8 8.8 0 1 0 10.6 10.6z" />
    </svg>
  );
}

/** Ours, not the reference's — it has no card for sleep. A moon that has gone to bed. */
function Night() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...S} d="M19.3 14.9A8 8 0 0 1 9.4 5a8.2 8.2 0 1 0 9.9 9.9z" />
      <path {...S} strokeWidth={1.3} d="M15.4 3.6h3.4l-3.4 3.6h3.4" />
    </svg>
  );
}

const BY_KEY: Record<string, () => ReactElement> = {
  OVERTHINKING: Cloud,
  MOVE_ON_FROM_BREAKUP: Heart,
  MANAGE_ANGER: Bolt,
  RELATIONSHIPS: People,
  CONFIDENCE: Person,
  WEALTH_MINDSET: Bars,
  DISCIPLINE: Leaf,
  GOALS: Moon,
  SLEEP: Night,
};

function Magnifier() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle {...S} cx="10.8" cy="10.8" r="6.2" />
      <path {...S} d="m15.4 15.4 4.2 4.2" />
    </svg>
  );
}

function PlayTriangle() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path {...S} d="M7.4 4.8v14.4L19.2 12z" />
    </svg>
  );
}

/** The four steps of the loop. Heart and Bars are the same marks the feelings grid uses. */
const BY_STEP: Record<string, () => ReactElement> = {
  CHOOSE: Magnifier,
  PLAY: PlayTriangle,
  REFLECT: Heart,
  RETURN: Bars,
};

export function StepIcon({ name }: { name: string }) {
  const Icon = BY_STEP[name] ?? Magnifier;
  return (
    <span className="step-icon" aria-hidden="true">
      <Icon />
    </span>
  );
}

export function FeelingIcon({ name }: { name: string }) {
  const Icon = BY_KEY[name] ?? Cloud;
  return (
    <span className="feeling-icon">
      <Icon />
    </span>
  );
}
